/**
 * Chips for a message's attachments on the Candidate Requests tab.
 *
 * The queue carries METADATA only (filename, size, mime, attachmentId) — clicking a chip fetches
 * the bytes for that one attachment with the recruiter's own Gmail token, then:
 *   - raster images open in a lightbox (the proxy's Content-Type allowlist decides what counts),
 *   - PDFs open in a new browser tab,
 *   - everything else downloads as an opaque blob (the proxy already forced
 *     application/octet-stream on anything not inline-safe).
 *
 * Bytes stay in MEMORY (object URLs revoked on unmount/close) — same rule as requestsCache:
 * candidate content never lands in localStorage. Filenames are candidate-controlled text and are
 * rendered as text; nothing here interprets them.
 *
 * Inline images (signature logos, cid-referenced) are folded behind one muted toggle: a recruiter
 * looking for "the photos the candidate sent" should not wade through logo.png ×4.
 */
import { useEffect, useRef, useState } from 'react';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import { FileText, Image, Loader2, Paperclip } from 'lucide-react';
import type { InboundAttachment } from '../../types/taActionBoard';
import {
  AttachmentTooLarge,
  fetchAttachment,
  GmailSessionExpired,
} from '../../services/taInboundApi';
import { useGmailConnection } from './GmailConnectionContext';
import { AttachmentViewer } from './AttachmentViewer';

const fmtSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const iconFor = (mimeType: string) => {
  const mime = mimeType.toLowerCase();
  const cls = 'h-3.5 w-3.5 shrink-0';
  if (mime.startsWith('image/')) return <Image className={cls} />;
  if (mime === 'application/pdf') return <FileText className={cls} />;
  return <Paperclip className={cls} />;
};

export function AttachmentChips({
  threadId,
  attachments,
  gmailUrl,
}: {
  threadId: string;
  attachments: InboundAttachment[];
  /** Fallback when an attachment cannot be served here (too large): open the thread in Gmail. */
  gmailUrl?: string | null;
}) {
  const gmail = useGmailConnection();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, { text: string; href?: string }>>({});
  const [showInline, setShowInline] = useState(false);
  const [viewer, setViewer] = useState<{ objectUrl: string; filename: string } | null>(null);
  // Every object URL minted here, revoked on unmount — a PDF tab keeps its document once loaded.
  const urlsRef = useRef<string[]>([]);
  useEffect(
    () => () => {
      urlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    },
    []
  );

  const real = attachments.filter((a) => !a.inline);
  const inline = attachments.filter((a) => a.inline);
  if (attachments.length === 0) return null;

  const open = async (a: InboundAttachment) => {
    if (busyId) return;
    // Type and size class only — never the filename (it is candidate-supplied text).
    void logAnalyticsEvent('ta_requests_attachment_opened', {
      mime_category: `${a.mimeType || ''}`.split('/')[0] || 'unknown',
      inline: Boolean(a.inline),
      size_bucket: a.sizeBytes > 5_000_000 ? 'over_5mb' : a.sizeBytes > 1_000_000 ? '1_5mb' : 'under_1mb',
    });
    setBusyId(a.attachmentId);
    setErrors((prev) => ({ ...prev, [a.attachmentId]: undefined as never }));
    try {
      const token = gmail.token || (await gmail.connect());
      if (!token) throw new GmailSessionExpired();
      const blob = await fetchAttachment(
        { threadId, messageId: a.messageId, attachmentId: a.attachmentId },
        token
      );
      const objectUrl = URL.createObjectURL(blob);
      urlsRef.current.push(objectUrl);
      if (blob.type.startsWith('image/')) {
        setViewer({ objectUrl, filename: a.filename || 'attachment' });
      } else if (blob.type === 'application/pdf') {
        // The click's user activation can be spent by the awaited fetch (slow file, Safari),
        // making window.open return null with no visible effect — fall back to a download,
        // which programmatic anchor clicks are allowed to do without activation.
        const tab = window.open(objectUrl, '_blank', 'noopener');
        if (!tab) {
          const link = document.createElement('a');
          link.href = objectUrl;
          link.download = a.filename || 'attachment';
          link.click();
        }
      } else {
        // Opaque download — the proxy forced application/octet-stream on anything not inline-safe.
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = a.filename || 'attachment';
        link.click();
      }
    } catch (err) {
      if (err instanceof AttachmentTooLarge) {
        setErrors((prev) => ({
          ...prev,
          [a.attachmentId]: { text: err.message, href: err.gmailUrl || gmailUrl || undefined },
        }));
      } else if (err instanceof GmailSessionExpired) {
        setErrors((prev) => ({
          ...prev,
          [a.attachmentId]: { text: 'Gmail session expired — reconnect on the board header.' },
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          [a.attachmentId]: {
            text: 'Could not fetch it just now.',
            href: gmailUrl || undefined,
          },
        }));
      }
    } finally {
      setBusyId(null);
    }
  };

  const chip = (a: InboundAttachment) => {
    const error = errors[a.attachmentId];
    return (
      <span key={a.attachmentId} className="inline-flex max-w-full items-center gap-1">
        <button
          type="button"
          onClick={() => void open(a)}
          disabled={busyId === a.attachmentId}
          className="inline-flex max-w-full items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-60"
          title={a.filename || 'attachment'}
        >
          {busyId === a.attachmentId ? (
            <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
          ) : (
            iconFor(a.mimeType)
          )}
          <span className="max-w-[16rem] truncate">{a.filename || 'attachment'}</span>
          {a.sizeBytes > 0 && <span className="text-gray-400">· {fmtSize(a.sizeBytes)}</span>}
        </button>
        {error && (
          <span className="text-xs text-red-700">
            {error.text}
            {error.href && (
              <>
                {' '}
                <a href={error.href} target="_blank" rel="noreferrer" className="underline">
                  Open in Gmail ↗
                </a>
              </>
            )}
          </span>
        )}
      </span>
    );
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-no-toggle>
      {real.map(chip)}
      {inline.length > 0 &&
        (showInline ? (
          inline.map(chip)
        ) : (
          <button
            type="button"
            onClick={() => setShowInline(true)}
            className="text-xs text-gray-400 underline hover:text-gray-600"
          >
            +{inline.length} inline image{inline.length === 1 ? '' : 's'}
          </button>
        ))}
      {viewer && (
        <AttachmentViewer
          objectUrl={viewer.objectUrl}
          filename={viewer.filename}
          onClose={() => {
            URL.revokeObjectURL(viewer.objectUrl);
            urlsRef.current = urlsRef.current.filter((u) => u !== viewer.objectUrl);
            setViewer(null);
          }}
        />
      )}
    </div>
  );
}
